import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { resolveV2ConsoleTarget } from '@/utils/v2ConsoleTarget'

// Hosts where the v2 Console preview banner is shown, each mapped to the v2 URL
// to link to. Keyed on `hostname` (port-agnostic). As each env's v2 console goes
// live, add its host (staging: console.sncloud-stg.dev, prod:
// console.streamnative.cloud). Local dev (console.local.sn, which runs against
// the TEST backend via USE_TEST_ENV) points at the test v2 console — dev has no
// v2 of its own.
const V2_PREVIEW_URL_BY_HOST: Record<string, string> = {
  'console.test.cloud.gcp.streamnative.dev': 'https://v2.console.test.cloud.gcp.streamnative.dev',
  'console.sncloud-stg.dev': 'https://v2.console.sncloud-stg.dev',
  'console.streamnative.cloud': 'https://v2.console.streamnative.cloud',
  'console.local.sn': 'https://v2.console.test.cloud.gcp.streamnative.dev'
}

export const V2_BANNER_ID = 'v2-console-preview-banner'

// Module-scoped so App.vue (renders the banner) and MainLayout.vue (reserves the
// top space) share ONE reactive dismissal flag. Initialized from — and written
// back to — the same localStorage key DsBanner persists to, so dismissal sticks
// across reloads and both consumers react to a close in the same tick (banner
// hides AND the reserved top space is released together).
const dismissed = ref(localStorage.getItem(V2_BANNER_ID) === 'close')
export function dismissV2Banner() {
  dismissed.value = true
  localStorage.setItem(V2_BANNER_ID, 'close')
}

// Shared by App.vue (renders the banner) and MainLayout.vue (reserves the top
// space so the sticky banner doesn't overlap page content). Hidden on the
// SignUp/login page and once dismissed.
export function useV2Banner() {
  const route = useRoute()
  const { activeClusterName } = useCluster()
  const { activeKafkaClusterName } = useKafkaCluster()
  const { activeWorkspaceName } = useWorkspace()
  const { activeTopicDetail } = useTopic()
  const v2ConsoleOrigin = V2_PREVIEW_URL_BY_HOST[window.location.hostname] ?? ''
  const v2ConsoleTarget = computed(() =>
    resolveV2ConsoleTarget({
      routeName: typeof route.name === 'string' ? route.name : null,
      params: route.params as Record<string, unknown>,
      query: route.query as Record<string, unknown>,
      pulsarClusterName: activeClusterName.value,
      kafkaClusterName: activeKafkaClusterName.value,
      workspaceName: activeWorkspaceName.value,
      topicPersistent: activeTopicDetail.value?.persistency
    })
  )
  const v2ConsoleUrl = computed(() =>
    v2ConsoleOrigin ? `${v2ConsoleOrigin}${v2ConsoleTarget.value.path}` : ''
  )
  const showV2Banner = computed(
    () =>
      !dismissed.value &&
      route.name !== 'SignUp' &&
      !!V2_PREVIEW_URL_BY_HOST[window.location.hostname]
  )
  // Persistent nav entry (left of the user menu) — appears ONLY after the banner
  // has been dismissed, so there's still a way to reach v2 once the announcement
  // is closed.
  const showV2NavLink = computed(
    () => dismissed.value && route.name !== 'SignUp' && !!v2ConsoleOrigin
  )
  return {
    v2ConsoleUrl,
    v2ConsoleTarget,
    showV2Banner,
    showV2NavLink,
    dismissV2Banner
  }
}
